import { ISimulationScoreCategory } from '@/types';
import { ScoreRowCard } from '@/components/Score';
import { SimulationScoreFeedbackItem } from '../SimulationScoreFeedbackItem';
import { SimulationScoreFeedbackProps } from '../types';
import { SKILL_LEVELS, TAG_LABELS } from '@/constants';
import { useMemo } from 'react';

/**
 * Component to display feedback based on the simulation score.
 * It renders a list of ScoreRowCards for each score category, showing feedback and scores.
 */
export const SimulationScoreFeedback: React.FC<SimulationScoreFeedbackProps> = ({
  complianceFeedback,
  currentUser = null,
  isLoading = false,
  isPrinting = false,
  score = null,
  simulation = null,
}) => {
  /**
   * Memoized value for the feedback to display, which includes the score
   * categories and compliance feedback if available. If compliance feedback is
   * provided, it is added as an additional category to the feedback list.
   */
  const feedbackToDisplay = useMemo((): ISimulationScoreCategory[] => {
    const categories = score?.categories || [];
    if (complianceFeedback) {
      return [
        ...categories,
        {
          score: 0,
          type: 'violation',
          title: TAG_LABELS.COMPLIANCE_REMINDER,
          content: [
            {
              title: complianceFeedback.title,
              items: complianceFeedback.items,
            },
          ],
        },
      ];
    }

    return categories;
  }, [complianceFeedback, score]);

  // If loading, show placeholder ScoreRowCards
  if (isLoading) {
    return Array.from({ length: 3 }).map((_, index) => (
      <ScoreRowCard
        category={''}
        isLoading={true}
        key={index}
        score={{ value: 0, maxValue: 0, type: 'numeric', passed: false }}
        type={'numeric'}
      />
    ));
  }

  // Render feedback items for each category
  return feedbackToDisplay.map((category, i) => (
    <SimulationScoreFeedbackItem
      key={i}
      category={category}
      categoryPassed={category.pass || false}
      categorySkillLevel={category.skillLevel || SKILL_LEVELS.BEGINNER}
      categoryType={category.type || 'numeric'}
      isPrinting={isPrinting}
      scoreCardId={score?.id || 0}
      sessionId={score?.sessionId || 0}
      sharedWithManager={score?.sharedWithManager || false}
      simulationType={simulation?.type || 'role-play'}
      userId={currentUser?.uuid || ''}
    />
  ));
};

SimulationScoreFeedback.displayName = 'SimulationScoreFeedback';

export default SimulationScoreFeedback;
